import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import {
  GatsbyImage,
  getImage,
  IGatsbyImageData,
  ImageDataLike,
} from 'gatsby-plugin-image';
import { Heading } from '../../../typography';
import useCollectionData from '../../../../hooks/useCollectionData';
import type Directus from '../../../../types/directus';
import * as style from './style.module.scss';

interface IExampleProps {
  title: string;
  content: string;
  image: ImageDataLike;
}

const Example = ({ title, content, image }: IExampleProps) => (
  <li className={style.example}>
    <article className={style.example__inner}>
      <figure className={style.example__figure}>
        {image && (
          <GatsbyImage
            className={style.example__image}
            image={getImage(image) as IGatsbyImageData}
            alt=""
          />
        )}
      </figure>
      <main className={style.example__main}>
        <h1 className={style.example__title}>{title}</h1>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </main>
    </article>
  </li>
);

interface IExampleItemsQuery {
  support_us: {
    examples_title: string;
    examples: Array<{
      id: string;
      status: Directus.Status;
      title: string;
      content: string;
      image: {
        imageFile: {
          childImageSharp: { gatsbyImageData: IGatsbyImageData };
        };
      };
      translations: Directus.Translations<{
        title: string;
        content: string;
      }>;
    }>;
    translations: Directus.Translations<{ examples_title: string }>;
  };
}

const SupportUsExamples = () => {
  const data: Directus.Query<IExampleItemsQuery> =
    useStaticQuery(graphql`
      query SupportUsExamplesQuery {
        directus {
          support_us {
            examples_title
            examples {
              id
              status
              title
              content
              image {
                id
                imageFile {
                  id
                  childImageSharp {
                    id
                    gatsbyImageData(quality: 90, placeholder: BLURRED)
                  }
                }
              }
              translations(
                filter: {
                  title: { _nempty: true }
                  content: { _nempty: true }
                }
              ) {
                title
                content
              }
            }
            translations(
              filter: { examples_title: { _nempty: true } }
            ) {
              examples_title
            }
          }
        }
      }
    `);
  const { examples } = data.directus.support_us;

  const { examples_title } = useCollectionData(
    data.directus.support_us
  );

  const exampleItems = examples
    .filter((example) => example.status === 'published')
    .map((example) => {
      example = useCollectionData(example);

      return (
        <Example
          key={example.id}
          title={example.title}
          content={example.content}
          image={
            example.image.imageFile.childImageSharp?.gatsbyImageData
          }
        />
      );
    });

  return (
    <div className={style.examples}>
      <Heading priority={2} alignText="center">
        {examples_title}
      </Heading>
      <ul className={style.examples__list}>{exampleItems}</ul>
    </div>
  );
};

export default SupportUsExamples;
